/**
 * Popup HTML builders for MapContainer's Leaflet popups. Pure string helpers —
 * they escape untrusted values and honour NO-FALLBACK (missing = em-dash /
 * "unavailable" copy, never a fabricated value).
 */
import type { CommonTranslations } from '@/i18n/types';

export type MapPopupCopy = CommonTranslations['mapPopup'];

export function escapePopupText(value: unknown): string {
  const escapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  return String(value).replace(/[&<>"']/g, (char) => escapeMap[char] ?? char);
}

export function interpolatePopupCopy(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const value = values[key];
    return value == null ? match : String(value);
  });
}

export function isPopupValueAvailable(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string') return true;

  const normalized = value.trim().toLowerCase();
  return (
    normalized !== '' &&
    normalized !== '-' &&
    normalized !== '—' &&
    normalized !== 'na' &&
    normalized !== 'n/a' &&
    normalized !== 'unknown' &&
    normalized !== 'tuntematon'
  );
}

export function popupUnavailable(copy: MapPopupCopy): string {
  return `<span class="stand-popup__unavailable">${escapePopupText(copy.unavailable)}</span>`;
}

export function popupTextValue(value: unknown, copy: MapPopupCopy): string {
  return isPopupValueAvailable(value) ? escapePopupText(value) : popupUnavailable(copy);
}

export function popupPlainTextValue(value: unknown, copy: MapPopupCopy): string {
  return isPopupValueAvailable(value) ? String(value) : copy.unavailable;
}

export function popupNumericValue(
  value: unknown,
  formatter: (value: number) => string,
  copy: MapPopupCopy,
): string {
  if (!isPopupValueAvailable(value)) {
    return popupUnavailable(copy);
  }
  if (typeof value !== 'number' && typeof value !== 'string') {
    return popupUnavailable(copy);
  }

  const numberValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numberValue)) {
    return popupUnavailable(copy);
  }

  return escapePopupText(formatter(numberValue));
}

export function popupRow(label: string, valueHtml: string): string {
  return `
    <div class="stand-popup__row">
      <span class="stand-popup__label">${escapePopupText(label)}</span>
      <span class="stand-popup__value">${valueHtml}</span>
    </div>
  `;
}
